import { useSignIn, useSignUp } from "@clerk/react";
import { legalLinks } from "@luckymenu/core/client";
import { useTranslation } from "@luckymenu/i18n/react";
import { useEmailCodeFlow } from "@luckymenu/staff-core";
import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	LegalLinks,
	Separator,
} from "@luckymenu/ui-web";
import { type FormEvent, useState } from "react";
import { env } from "../env";
import { Brand } from "./brand";

function GoogleIcon() {
	return (
		<svg viewBox="0 0 24 24" aria-hidden className="size-5">
			<path
				fill="#4285F4"
				d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.2h6a5 5 0 0 1-2.2 3.3v2.7h3.5c2.1-1.9 3.3-4.7 3.3-8z"
			/>
			<path
				fill="#34A853"
				d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2.1v2.8A11 11 0 0 0 12 23z"
			/>
			<path fill="#FBBC05" d="M5.7 14.1a6.6 6.6 0 0 1 0-4.2V7.1H2.1a11 11 0 0 0 0 9.8l3.6-2.8z" />
			<path
				fill="#EA4335"
				d="M12 5.4c1.6 0 3.1.6 4.2 1.7l3.1-3.1A11 11 0 0 0 2.1 7.1l3.6 2.8C6.6 7.3 9.1 5.4 12 5.4z"
			/>
		</svg>
	);
}

/** The privacy policy and terms (P12-T21), while they are published. */
function legalLinkItems(t: (key: "privacy" | "terms") => string) {
	const links = legalLinks(env.APP_ENV, env.ROOT_DOMAIN);
	return links
		? [
				{ href: links.privacy, label: t("privacy") },
				{ href: links.terms, label: t("terms") },
			]
		: null;
}

/** Google, or an emailed code. No passwords. See auth-tenancy.md. */
export function SignInScreen() {
	const { t } = useTranslation(["auth", "errors"]);
	const { signIn } = useSignIn();
	const { signUp } = useSignUp();
	const flow = useEmailCodeFlow(signIn, signUp);
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [redirecting, setRedirecting] = useState(false);

	const google = async () => {
		setRedirecting(true);
		const { error } = await signIn.sso({
			strategy: "oauth_google",
			redirectUrl: "/",
			redirectCallbackUrl: "/sso-callback",
		});
		if (error) setRedirecting(false);
	};

	const submitEmail = (e: FormEvent) => {
		e.preventDefault();
		void flow.sendCode(email);
	};
	const submitCode = (e: FormEvent) => {
		e.preventDefault();
		void flow.verifyCode(code);
	};

	return (
		<main className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-surface p-4">
			<Brand />
			<Card className="w-full max-w-sm">
				<CardHeader>
					<CardTitle>
						<h1 className="text-xl">{t("signInTitle")}</h1>
					</CardTitle>
					<CardDescription>{t("staffTagline")}</CardDescription>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					{flow.step === "email" ? (
						<>
							<Button
								variant="outline"
								size="lg"
								className="w-full"
								onClick={() => void google()}
								disabled={redirecting}
							>
								<GoogleIcon />
								{t("signInWithGoogle")}
							</Button>
							<div className="flex items-center gap-3 text-xs text-muted-foreground">
								<Separator className="flex-1" />
								{t("or")}
								<Separator className="flex-1" />
							</div>
							<form className="flex flex-col gap-3" onSubmit={submitEmail} noValidate>
								<Label htmlFor="email">{t("emailLabel")}</Label>
								<Input
									id="email"
									type="email"
									inputMode="email"
									autoComplete="email"
									placeholder={t("emailPlaceholder")}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									aria-invalid={flow.error ? true : undefined}
									aria-describedby={flow.error ? "sign-in-error" : undefined}
								/>
								<Button type="submit" size="lg" className="w-full" disabled={flow.pending}>
									{t("sendCode")}
								</Button>
								{/* Clerk mounts its bot check here when a new email signs up. */}
								<div id="clerk-captcha" />
							</form>
						</>
					) : (
						<form className="flex flex-col gap-3" onSubmit={submitCode} noValidate>
							<p className="text-sm text-muted-foreground">
								{t("codeSentTo", { email: flow.email })}
							</p>
							<Label htmlFor="code">{t("codeLabel")}</Label>
							<Input
								id="code"
								inputMode="numeric"
								autoComplete="one-time-code"
								maxLength={8}
								value={code}
								onChange={(e) => setCode(e.target.value)}
								aria-invalid={flow.error ? true : undefined}
								aria-describedby={flow.error ? "sign-in-error" : undefined}
								autoFocus
							/>
							<Button type="submit" size="lg" className="w-full" disabled={flow.pending}>
								{t("verifyCode")}
							</Button>
							<div className="flex justify-between">
								<Button type="button" variant="link" className="px-0" onClick={flow.restart}>
									{t("useAnotherEmail")}
								</Button>
								<Button
									type="button"
									variant="link"
									className="px-0"
									onClick={() => void flow.resend()}
									disabled={flow.pending}
								>
									{t("resendCode")}
								</Button>
							</div>
						</form>
					)}
					{flow.error ? (
						<p id="sign-in-error" role="alert" className="text-sm text-destructive">
							{t(flow.error)}
						</p>
					) : null}
				</CardContent>
			</Card>
			<LegalLinks links={legalLinkItems(t)} />
		</main>
	);
}
